import React from "react";
import dayjs from "dayjs";
import { EventType, HandoverStatus } from "constants/field-values";
import {
	useGetHandoverActivitiesQuery,
	GetHandoverActivitiesQuery
} from "graphql/queries/get-handover-activities.generated";

type ActivityItem = NonNullable<NonNullable<GetHandoverActivitiesQuery["get_handover_activity"]>[number]>;

const BASE = process.env.PUBLIC_URL;

const ACTIVITY_ICONS = {
	created: `${BASE}/assets/images/icons/user-circle.svg`,
	submitted: `${BASE}/assets/images/icons/airplane-circle.svg`,
	feedback: `${BASE}/assets/images/icons/clock-circle.svg`,
	resubmitted: `${BASE}/assets/images/icons/note-circle.svg`
};

type ActivityContent = { action: string; icon: string };

function getActivityContent(
	activity: ActivityItem,
	allActivities: ActivityItem[],
	index: number
): ActivityContent | null {
	const { event_type, new_values, old_values, user, created_org_name, created_property_name } = activity;
	const atOrg = user.org_name ? ` at ${user.org_name}` : "";

	if (event_type === EventType.INSERT) {
		if (created_org_name && created_property_name) {
			return {
				action: `${atOrg} created the handover, a new organization (${created_org_name}), and a new property (${created_property_name}).`,
				icon: ACTIVITY_ICONS.created
			};
		}
		if (created_property_name) {
			return {
				action: `${atOrg} created the handover and a new property (${created_property_name}).`,
				icon: ACTIVITY_ICONS.created
			};
		}
		if (created_org_name) {
			return {
				action: `${atOrg} created the handover and a new organization (${created_org_name}).`,
				icon: ACTIVITY_ICONS.created
			};
		}
		return {
			action: `${atOrg} created the handover.`,
			icon: ACTIVITY_ICONS.created
		};
	}

	if (event_type === EventType.UPDATE) {
		const oldStatus = old_values?.status;
		const newStatus = new_values?.status;

		if (newStatus === HandoverStatus.PUBLISHED) {
			return {
				action: `${atOrg} accepted and published the handover.`,
				icon: ACTIVITY_ICONS.submitted
			};
		}

		if (oldStatus === HandoverStatus.PENDING_CUSTOMER && newStatus === HandoverStatus.PENDING_VENDOR) {
			return {
				action: "Reviewed the handover and provided feedback.",
				icon: ACTIVITY_ICONS.feedback
			};
		}

		if (newStatus === HandoverStatus.PENDING_CUSTOMER) {
			// Activities are sorted desc, so older entries are at higher indexes.
			// If any activity after this one sent it back to the vendor, this is a resubmission.
			const wasReviewed = allActivities
				.slice(index + 1)
				.some(
					a =>
						a.old_values?.status === HandoverStatus.PENDING_CUSTOMER &&
						a.new_values?.status === HandoverStatus.PENDING_VENDOR
				);

			if (wasReviewed) {
				return {
					action: "Updated the handover based on feedback.",
					icon: ACTIVITY_ICONS.resubmitted
				};
			}

			if (oldStatus === HandoverStatus.VENDOR_DRAFT || oldStatus === HandoverStatus.PENDING_VENDOR) {
				return {
					action: `${atOrg} submitted the handover for review.`,
					icon: ACTIVITY_ICONS.submitted
				};
			}
		}
	}

	return null;
}

interface HandoverActivityProps {
	handoverId: string;
}

const HandoverActivity: React.FC<HandoverActivityProps> = ({ handoverId }) => {
	const { data } = useGetHandoverActivitiesQuery({
		skip: !handoverId,
		variables: { handoverId }
	});

	const activities = data?.get_handover_activity?.filter(Boolean) as ActivityItem[] ?? [];

	if (!activities.length) return null;

	return (
		<div className="hac-section">
			<div className="hd-section-title">Activity</div>
			<div className="hac-card">
				{activities.map((item, index) => {
					const content = getActivityContent(item, activities, index);
					if (!content) return null;

					const fullName = `${item.user.first_name} ${item.user.last_name}`;
					const isLast = index === activities.length - 1;

					return (
						<div key={item.id} className={`hac-row${isLast ? "" : " hac-row--divider"}`}>
							<img className="hac-icon" src={content.icon} alt="" />
							<div className="hac-content">
								<div className="hac-text">
									<span className="hac-name">{fullName}</span> {content.action.trim()}
								</div>
								<span className="hac-timestamp">
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/clock-small.svg`} />
									{dayjs(item.created_at).format("MM/DD/YYYY, hh:mm a")}
								</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default HandoverActivity;
